import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Page from '../../../components/content/page-header';
import { Primary, Tertiary } from '../../../components/button/button';
import { FileInputLarge } from '../../../components/forms/file';
import { useForm, FieldValues } from 'react-hook-form';
import { alert } from '../../../components/toast/toast';
import { usePostFormDataMutation } from '../../../hooks/use-form-mutations';
import { bytesToSize } from '../../../utils/file';

type Progress = {
  loaded: number;
  total: number;
}

export const MapLayerImport = (): JSX.Element => {
  const navigate = useNavigate();
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm();
  const file = watch('file');
  const [progress, setProgress] = useState<Progress>({ loaded: 0, total: 0 });
  const onUploadProgress = (event: ProgressEvent): void => {
    setProgress({ loaded: event.loaded, total: event.total });
  };
  const postFormMutation = usePostFormDataMutation({ path: '/map-layers/import', cacheKey: '/map-layers', onUploadProgress  });

  const onSubmit = async (values: FieldValues): Promise<void> => {
    try {
      await postFormMutation.mutateAsync({ file: values.file });
      alert.success('Layer imported successfully');
      navigate('/map-layers');
    } catch {
      alert.error('Failed to upload file');
      setValue('file', undefined);
      setProgress({ loaded: 0, total: 0 });
    }
  };

  const loadedBytes = bytesToSize(progress.loaded);
  const totalBytes = bytesToSize(file?.[0]?.size as number || 0);

  return <div>
    <div className="row">
      <div className="col-9 bg-light left-col min-height-600">
        <Page.Header>
          <Page.Title>
            <Link to="/map-layers" className="page-title-link">
              Import Layer File
            </Link>
          </Page.Title>
          <Page.SubTitle>Import a layer that is not available within the Gold Master.</Page.SubTitle>
        </Page.Header>
        <form id="current-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="col-3">
            <FileInputLarge { ...register('file', { required: 'You must provide a value for File.' })} errors={errors} accept="application/json" />
            <span className="supported-file-format">Supported file format: JSON</span>
          </div>
          {file?.[0] && (
            <>
              <div className="upload-state">{postFormMutation.isLoading ? 'Uploading' : postFormMutation.isSuccess ? 'Uploaded' : 'Upload not started'}</div>
              <div className="upload-meta">
                {(loadedBytes > totalBytes ? totalBytes : loadedBytes).split(' ')[0] || 0} / {totalBytes}
              </div>
              <div className="col-10">
                <div className="progressbar-bg">
                  <div className="progress-current" style={{ width: `${progress.loaded / progress.total * 100}%` }} />
                </div>
              </div>
            </>
          )}
        </form>
      </div>
      <div className="col-3 d-flex flex-column">
        <div className="px-5">
          <div className="mb-3">
            <Primary form="current-form" id="1" type="submit" disabled={postFormMutation.isLoading}>Save</Primary>
          </div>
          <div className="mb-3">
            <Tertiary onClick={(): void => navigate('/map-layers')} disabled={postFormMutation.isLoading}>Cancel</Tertiary>
          </div>
        </div>
      </div>
    </div>
  </div>;
};
